import React from 'react';
import './Legal.css';

function TermsAndConditions() {
  return (
    <div className="legal page-shell">
      <span className="tag">Legal</span>
      <h1 className="page-title">Terms and Conditions</h1>
      <p className="page-lead">Last updated: August 2, 2026</p>

      <div className="legal-section section-card">
        <h2>1. Acceptance of Terms</h2>
        <p>
          By accessing, purchasing from, subscribing to, or using Vix Algo Trading Store, VixAlgoBuilder,
          VixAI, VixPremium, community features, product downloads, or support services, you agree to
          these Terms and Conditions. If you do not agree, do not use the site or services.
        </p>

        <h2>2. Products, Builder, and Delivery</h2>
        <p>
          We sell digital trading products and provide Builder tools, including Expert Advisors,
          indicators, source code, saved strategies, backtests, strategy reports, and MQ5 exports.
          Delivery is provided electronically after payment or subscription access is confirmed.
          You are responsible for ensuring your platform, broker account, symbol, history, inputs, and
          trading environment support the files and settings provided.
        </p>

        <h2>3. Licensing and Subscriptions</h2>
        <p>
          Licenses are issued per product and limited to two devices per license, unless otherwise stated.
          Licenses are non-transferable. We reserve the right to revoke licenses in cases of abuse,
          fraud, or violation of these terms.
        </p>
        <p>
          VixPremium subscriptions provide access to premium Builder features, MQ5 export, saved strategy
          workflows, VixAI, and community features while the subscription is active. Subscription billing,
          renewals, cancellations, and payment disputes are handled through the payment provider or the
          account billing tools we make available. Cancelling a subscription stops future renewal charges
          but does not automatically refund prior payments unless required by law or approved by us.
        </p>
        <p>
          The Builder requires a logged-in account. Free accounts may create and test strategies, save one
          strategy, and export one MQ5 file per calendar month. VixPremium accounts may use VixAI, export
          MQ5 files without a monthly limit, and save up to 100 strategies.
        </p>

        <h2>4. Accounts</h2>
        <p>
          You may create an account to manage purchases, licenses, subscriptions, saved strategies,
          Builder activity, VixAI conversations, community posts, and support. You are responsible for
          maintaining the confidentiality of your credentials and for all activity on your account.
          You may request account deletion at any time.
        </p>

        <h2>5. Payments</h2>
        <p>
          Payments and subscriptions are processed through approved payment providers. You agree to
          provide accurate billing information and authorize the stated purchase amount or subscription
          charges. Access may be paused or removed if payment fails, is reversed, or is suspected of fraud.
        </p>

        <h2>6. Refunds</h2>
        <p>
          Due to the digital nature of our products, Builder exports, and subscription access, all sales
          are final unless required by law. Refunds may be considered for exceptional cases such as
          non-delivery, billing error, or licensing errors reported within 7 days of purchase.
        </p>

        <h2>7. Support</h2>
        <p>
          Support is provided via ticket or chat for account holders. We aim to respond within 24-48 hours
          on business days. Support does not include a promise that a strategy will be profitable, broker-
          compatible, or accepted by every trading platform.
        </p>

        <h2>8. Community Rules</h2>
        <p>
          Logged-in free users may read community threads. VixPremium is required to post threads, comment,
          upload or attach files and links, download shared strategy files, import shared strategies into the
          Builder, or save community strategies. You are responsible for content you post. You may not upload
          illegal, malicious, misleading, infringing, abusive, or spam content. We may remove, edit, hide,
          restrict, or moderate community content and accounts at our discretion. By posting content, you
          grant us a non-exclusive right to host, display, moderate, and make that content available inside
          the community features.
        </p>

        <h2>9. VixAI and Builder Output</h2>
        <p>
          VixAI is an assistant for strategy drafting, Builder setup, explanation, validation guidance,
          and workflow automation. AI output can be incomplete or incorrect. You must review, test, and
          validate all strategies, rules, settings, code, reports, and exported EAs before using them.
          VixAI and VixAlgoBuilder do not provide financial advice and do not guarantee profit, compile
          success, MT5 parity, broker acceptance, or live-trading performance.
        </p>

        <h2>10. Importing Your Own Expert Advisors</h2>
        <p>
          You can upload MQL5 source for an Expert Advisor and have us compile and backtest it on our
          servers. You keep every right you already had in that code — uploading it gives us
          permission to run it for you and to show you the result, and nothing more. We do not claim
          ownership of it, we do not sell it, we do not share it with other users, and we do not use
          it to train models. The source is deleted once the run finishes; the resulting report stays
          with your account.
        </p>
        <p>
          You must have the right to upload what you upload. Do not import an EA whose licence
          forbids copying it to a third-party service, and do not import someone else's paid product
          in order to inspect or reproduce it. You are responsible for that, not us.
        </p>
        <p>
          Our engine implements a large part of the MQL5 language, but not all of it. An EA may fail
          to run, or may run and produce results that differ from your terminal, and we do not
          guarantee that any particular EA can be imported, compiled, executed, or reproduced
          faithfully. Where an EA uses something we have not implemented, we record the name of the
          missing feature so we can build it, as described in our Privacy Policy. Backtests run on
          historical data with the limitations set out in the FAQ, and are not a prediction of live
          results.
        </p>

        <h2>11. Prohibited Use</h2>
        <p>
          You may not resell, redistribute, reverse engineer, or publicly share our digital products or
          license keys. You may not share premium-only content outside permitted areas, abuse VixAI,
          upload malicious files, scrape the site, bypass access controls, tamper with Builder systems,
          or use the platform to mislead other users.
        </p>

        <h2>12. Intellectual Property</h2>
        <p>
          All content, code, and product materials remain the property of Vix Algo Trading Store or its
          licensors. Purchase grants a limited, revocable, non-exclusive license to use the product.
          Strategies you create remain yours, but platform templates, Builder logic, VixAI systems, site
          design, documentation, and proprietary code remain ours or our licensors' property.
        </p>

        <h2>13. Cloud Signals, Following, and the MT5 Copier</h2>
        <p>
          A cloud signal is a saved strategy that we run on live market data in a simulated account.
          It is a record of what a strategy would have done — not a recommendation, and not
          financial advice. Following a signal requires an active VixPremium plan and is included
          in that plan; signal creators are not paid by us or by you, and no payment passes between
          users.
        </p>
        <p>
          A track record shown on this site separates results proven forward since deployment from
          replayed backtest history used to fill the chart. Past performance of either kind does not
          predict future results, and a signal marked "Verified" only means it has met a minimum
          age and trade count — it is not our endorsement, assessment, or guarantee of that
          strategy.
        </p>
        <p>
          <strong>The MT5 copier places real orders in your own trading account.</strong> If you
          install and enable it, you are choosing to let a strategy trade your money. You remain
          solely responsible for the account it is attached to, the position sizing you configure,
          the broker you use, and every resulting trade. Signals can be delayed, missed, duplicated,
          or acted on at a worse price than the signal itself received — for reasons including your
          internet connection, your terminal being closed, broker execution, slippage, spread,
          swaps, symbol differences between brokers, and market gaps. We do not guarantee that any
          signal reaches your terminal or is executed at all.
        </p>
        <p>
          We may suspend, revoke, or rate-limit copier tokens and signal access to protect the
          service, and we may stop, pause, or remove any cloud signal at any time. You should not
          rely on a cloud signal or the copier as your only risk control. Use an account whose loss
          you can absorb, and verify behaviour on a demo account first.
        </p>

        <h2>14. Affiliate Links and Third-Party Services</h2>
        <p>
          Some links on this site are affiliate links — including links to open an account with
          Deriv. If you open an account or trade through one of them we may receive a
          commission, at no additional cost to you. Links are marked where they appear.
        </p>
        <p>
          A commission is not a recommendation. We are not a broker, we do not hold your funds,
          and we have no role in your relationship with any broker — your account, deposits,
          withdrawals, execution, and any dispute are strictly between you and that provider,
          under their terms. Choose a broker on its own merits and its regulatory standing in
          your jurisdiction, not on the fact that we link to it.
        </p>
        <p>
          Pages may also embed third-party content such as market-quote widgets. That content is
          served by its provider, may set its own cookies, and is subject to that provider&apos;s
          terms and privacy policy rather than ours. Quoted prices are indicative only and are
          not the prices you will trade at.
        </p>

        <h2>15. Risk Disclaimer</h2>
        <p>
          Trading involves substantial risk. Backtests, reports, strategy exports, AI suggestions,
          cloud signals, copied trades, and community posts are tools and information only. They do
          not constitute financial advice. You are solely responsible for your trading decisions,
          risk controls, broker checks, platform setup, and outcomes.
        </p>

        <h2>16. Limitation of Liability</h2>
        <p>
          To the fullest extent permitted by law, we are not liable for indirect, incidental, or
          consequential damages, including loss of profits, data, or trading opportunities.
        </p>

        <h2>17. Changes to Terms</h2>
        <p>
          We may update these terms periodically. Continued use of the site constitutes acceptance
          of the updated terms.
        </p>

        <h2>18. Contact</h2>
        <p>
          Questions about these terms can be sent to support@vixalgotrading.com.
        </p>
      </div>
    </div>
  );
}

export default TermsAndConditions;
